import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import AddToGroupIcon from '@wix/da-ds/pkg/Icons/24x24/AddToGroupFill';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, AddToGroupClickBiEvent } from '@wix/da-bi/pkg/events';
import { useCustomNotifications } from '../../../Notifications/CustomNotificationContext';
import { NotificationType } from '../../../Notifications';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import ActionBarToggleButton from '../../_partials/items/ActionBarToggleButton';
import { FirstTimeFlag } from '../../../redux/firstTimeFlags';
import { useFirstTimeFlag } from '../../../redux/firstTimeFlags/hooks';
import SophisticatedFtueTooltip from '../../../Popover/Preset/SophisticatedFtueTooltip';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';

const addToGroupNotificationType = [NotificationType.AddToGroup];

export interface Props {
  deviation: PapiDeviation;
  activated?: boolean;
  withTooltip?: boolean;
  actionBarType?: 'primary' | 'secondary';
  onClick?: (e?: any) => void;
}

export const AddToGroupButton: React.FC<React.PropsWithChildren<Props>> = ({
  activated,
  withTooltip,
  deviation,
  onClick,
}) => {
  const { t } = useTranslation();

  const { restrictToLoggedIn } = useRestrictToLoggedIn();

  // a ftue tooltip appears the first time a user submits a deviation
  // that suggests adding the deviation to a group
  const { hasFlag: hasAddToGroupFtue } = useFirstTimeFlag(
    FirstTimeFlag.IsDevpageAddToGroupNotShown
  );
  const currentUrl =
    typeof window !== 'undefined' ? new URL(window.location.href) : undefined;
  const hasNewSubmissionUrlParam =
    !!currentUrl?.searchParams.get('ga_submit_new');
  const showingFtueTooltip = hasAddToGroupFtue && hasNewSubmissionUrlParam;

  // otherwise, addToGroup notificiations or the simple tooltip is shown
  const { notifications } = useCustomNotifications(addToGroupNotificationType);
  const isShowingTooltip = withTooltip && !notifications.length;
  const tooltipText = isShowingTooltip ? t('actionbar.addToGroup') : undefined;

  const button = (
    <ActionBarToggleButton
      activated={activated}
      onClick={restrictToLoggedIn(ReferralType.ADD_TO_GROUP, onClick)}
      biData={BiData<AddToGroupClickBiEvent>({
        evid: 340,
        deviationid: deviation.deviationId,
        sectionname: 'add_to_group',
      })}
      icon={AddToGroupIcon}
    />
  );

  return showingFtueTooltip ? (
    <SophisticatedFtueTooltip
      flag={FirstTimeFlag.IsDevpageAddToGroupNotShown}
      size="medium"
      placement="top"
      width={290}
      hasCloseButton
      prefix={
        <img
          loading="lazy"
          alt="IL44"
          src="https://st.deviantart.net/eclipse/promos/il4-5.svg"
        />
      }
      heading={t('deviationview.groupPrompt.title')}
      description={t('deviationview.groupPrompt.message')}
    >
      {button}
    </SophisticatedFtueTooltip>
  ) : (
    <ActionBarItemWrapper
      tooltip={tooltipText}
      notificationTypes={addToGroupNotificationType}
    >
      {button}
    </ActionBarItemWrapper>
  );
};
AddToGroupButton.displayName = 'AddToGroupButton';

export default AddToGroupButton;
